import { useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { useForm } from 'react-hook-form'
import { useMemo, useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { Check, X } from 'lucide-react'

import { useMutation } from '@hooks/graphql'
import RadioGroup from '@components/RadioGroup'
import SecureMarkdown from '@components/SecureMarkdown'
import { usePrompt } from '@hooks/prompt'
import { useFlashMessage } from '@components/FlashMessage'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@components/ui/form'
import { UPDATE_QUESTION_SUBMISSION_CACHE } from './hooks'

import BlockLayout from './BlockLayout'
import BlockActions from './BlockActions'

const CREATE_QUESTION_SUBMISSION_MUTATION = gql`
  mutation createQuestionSubmission($input: CreateQuestionSubmissionInput!) {
    createQuestionSubmission(input: $input) {
      errors {
        message
      }
      blockSubmission {
        content {
          ... on QuestionSubmission {
            question {
              id
              label
            }
            answer {
              id
              label
            }
            marks
          }
        }
      }
    }
  }
`

const useQuestionSubmission = ({
  blockId,
  submissionId,
  assignmentId,
  isInstantFeedback
}) => {
  const queryClient = useQueryClient()
  const { setFlashMessage } = useFlashMessage()

  const { mutateAsync: createQuestionSubmission, isLoading } = useMutation({
    gqlMutation: CREATE_QUESTION_SUBMISSION_MUTATION,
    onSuccess: (data) => {
      const newSubmission = data.createQuestionSubmission.blockSubmission.content

      UPDATE_QUESTION_SUBMISSION_CACHE(queryClient, submissionId, blockId, newSubmission)

      queryClient.invalidateQueries(['educatorProjectSubmission', submissionId])
    },
    onError: () => {
      setFlashMessage('Oops, we couldn\'t save your work. Please try again.', 'error')
    }
  })

  const submitAnswer = async (data) => {
    try {
      await createQuestionSubmission({
        input: { blockId, educatorProjectSubmissionId: submissionId, assignmentId, ...data }
      })
      return true
    } catch (error) {
      setFlashMessage('Oops, we couldn\'t save your work. Please try again.', 'error')
      return false
    }
  }

  return {
    submitAnswer,
    isLoading
  }
}

const QuestionBlock = ({
  blockId,
  submissionId,
  assignmentId,
  moveToNextBlock,
  moveToPreviousBlock,
  isFirstBlock,
  isLastBlock,
  openSubmitModal,
  isSubmissionClosed,
  submissionStatus,
  question,
  options,
  answerId,
  marks,
  markAllocation,
  isTimeUp,
  feedbackType
}) => {
  const isInstantFeedback = feedbackType === 'INSTANT'

  const projectDisabled = submissionStatus === 'COMPLETED' || submissionStatus === 'RETURNED' || isSubmissionClosed

  const { submitAnswer, isLoading } = useQuestionSubmission({
    blockId,
    submissionId,
    assignmentId,
    isInstantFeedback
  })

  const form = useForm({
    mode: 'onTouched',
    values: { answerId }
  })

  const {
    handleSubmit,
    formState: { isDirty },
    control,
    getValues
  } = form

  const instantFeedbackContent = useMemo(() => {
    if (!isInstantFeedback) return null

    if (answerId && marks !== undefined) {
      return {
        answerId,
        marks,
        options
      }
    }

    return null
  }, [isInstantFeedback, answerId, marks, options])

  usePrompt('You have unsaved changes. Are you sure you want to leave?', isDirty && !isLoading && !isLastBlock)

  const submit = async (formData) => {
    if (isTimeUp) {
      if (isLastBlock) openSubmitModal()
      else moveToNextBlock()
      return
    }

    let shouldNavigate = true
    if (isDirty) {
      const success = await submitAnswer(formData)
      if (isInstantFeedback) {
        // don't auto-navigate if instant feedback
        shouldNavigate = false
      } else {
        // if not instant feedback, auto-navigate if submission was successful
        shouldNavigate = success
      }
    }

    if (shouldNavigate) {
      if (isLastBlock) {
        openSubmitModal()
      } else {
        moveToNextBlock()
      }
    }
  }

  const handleBack = async () => {
    if (isTimeUp) {
      moveToPreviousBlock()
      return
    } else {
      if (isDirty) {
        const formData = getValues()
        const success = await submitAnswer(formData)
        if (success) {
          moveToPreviousBlock()
        }
      } else {
        moveToPreviousBlock()
      }
    }
  }

  const getSubmitButtonText = () => {
    if (submissionStatus === 'COMPLETED' || submissionStatus === 'RETURNED') {
      return isLastBlock ? 'Submitted' : 'Next'
    }

    if (isLoading) return 'Saving...'

    if (isInstantFeedback && isDirty && !isTimeUp) return 'Save'

    return isLastBlock ? 'Submit' : 'Next'
  }

  const effectiveAnswerId = isInstantFeedback && instantFeedbackContent ? instantFeedbackContent.answerId : answerId
  const effectiveMarks = isInstantFeedback && instantFeedbackContent ? instantFeedbackContent.marks : marks

  return (
    <Form className='h-full' {...form}>
      <form className='h-full' onSubmit={handleSubmit(submit)}>
        <BlockLayout>
          <BlockLayout.Actions>
            <BlockActions
              isFirstBlock={isFirstBlock}
              isLastBlock={isLastBlock}
              onBack={handleBack}
              isSubmitting={isLoading}
              isSubmissionClosed={isSubmissionClosed}
              submissionStatus={submissionStatus}
              buttonText={getSubmitButtonText()}
              tooltipText="Submissions are closed for this pathway"
            />
          </BlockLayout.Actions>

          <BlockLayout.Content>
            <SecureMarkdown content={question} />

            <If condition={markAllocation > 0}>
              <div className='flex justify-end w-full'>
                <div className='w-fit bg-muted text-muted-foreground px-2 py-1 rounded-md leading-tight text-sm'>
                  <span className='font-bold'>Marks: {markAllocation}</span>
                </div>
              </div>
            </If>

            <Choose>
              <When condition={projectDisabled}>
                <ul className='flex flex-col gap-2'>
                  <For each='option' of={options}>
                    <li
                      className={twMerge(
                        'flex border border-border p-3 rounded-lg',
                        effectiveAnswerId === option.id && 'border-primary bg-accent',
                        submissionStatus === 'RETURNED' && effectiveAnswerId === option.id && (
                          option.correct
                            ? 'bg-green-50 dark:bg-green-950 border-green-500 dark:border-green-400'
                            : 'bg-red-50 dark:bg-red-950 border-red-500 dark:border-red-400'
                        )
                      )}
                      key={option.id}
                    >
                      <SecureMarkdown content={option.label} />

                      <If condition={submissionStatus === 'RETURNED' && effectiveAnswerId === option.id}>
                        <Choose>
                          <When condition={option.correct}>
                            <Check className='ml-auto w-6 h-6 text-green-600 dark:text-green-400' />
                          </When>

                          <Otherwise>
                            <X className='ml-auto w-6 h-6 text-red-600 dark:text-red-400' />
                          </Otherwise>
                        </Choose>
                      </If>
                    </li>
                  </For>
                </ul>

                <If condition={submissionStatus === 'RETURNED'}>
                  <div className='w-fit bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-lg leading-tight mt-3'>
                    <span className='text-lg font-semibold text-gray-900 dark:text-gray-100'>{effectiveMarks} / {markAllocation}</span>
                  </div>
                </If>
              </When>

              <Otherwise>
                <FormField
                  control={control}
                  name='answerId'
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          {...field}
                          disabled={projectDisabled || isLoading || isTimeUp}
                          theme='secondary'
                        >
                          <For each='option' of={options}>
                            <RadioGroup.Option
                              className={twMerge(
                                'bg-white dark:bg-gray-900',
                                isInstantFeedback && instantFeedbackContent && effectiveAnswerId === option.id && (
                                  option.correct
                                    ? 'ring-2 ring-green-500 dark:ring-green-400 bg-green-50 dark:bg-green-950'
                                    : 'ring-2 ring-red-500 dark:ring-red-400 bg-red-50 dark:bg-red-950'
                                )
                              )}
                              label={
                                <div className="flex items-center justify-between w-full">
                                  <SecureMarkdown content={option.label} />
                                  <If condition={isInstantFeedback && instantFeedbackContent && effectiveAnswerId === option.id}>
                                    <Choose>
                                      <When condition={option.correct}>
                                        <Check className='ml-2 w-5 h-5 text-green-600 dark:text-green-400' />
                                      </When>
                                      <Otherwise>
                                        <X className='ml-2 w-5 h-5 text-red-600 dark:text-red-400' />
                                      </Otherwise>
                                    </Choose>
                                  </If>
                                </div>
                              }
                              value={option.id}
                            />
                          </For>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <If condition={isInstantFeedback && instantFeedbackContent}>
                  <div className='w-fit bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-lg leading-tight mt-3'>
                    <span className='text-lg font-semibold text-gray-900 dark:text-gray-100'>{effectiveMarks} / {markAllocation}</span>
                  </div>
                </If>
              </Otherwise>
            </Choose>
          </BlockLayout.Content>
        </BlockLayout>
      </form>
    </Form>
  )
}

export default QuestionBlock
